import { DynamicConfig } from '@domain/configuration/types';
import { useChatDynamicConfigLoader } from '@/experiences/sales-portal/blocs/loaders/use-chat-dynamic-config-loader';
import { useMemo } from 'react';
import { useSupplierDynamicConfigLoader } from '@/experiences/sales-portal/blocs/loaders/use-supplier-dynamic-config-loader';

export type { DynamicConfig, Experiment } from '@domain/configuration/types';

interface ChatSalesPortalDynamicConfigParams {
  chatUUID?: string | null;
  supplierUUID: string;
}

interface ChatSalesPortalDynamicConfigResult {
  chatDynamicConfig: DynamicConfig | null;
  loading: boolean;
  supplierDynamicConfig: DynamicConfig | null;
}

/**
 * Access dynamic config for a given chat / supplier pair.
 *
 * @param params - The configuration parameters
 * @param params.chatUUID - Optional chat identifier for chat-specific config
 * @param params.supplierUUID - The supplier identifier
 * @returns An object containing chat config, supplier config, and loading state
 *
 * @example
 * ```typescript
 * const { chatDynamicConfig, loading, supplierDynamicConfig } =
 *   useChatSalesPortalDynamicConfig({ chatUUID, supplierUUID });
 * ```
 */
export const useChatSalesPortalDynamicConfig = ({
  chatUUID,
  supplierUUID,
}: ChatSalesPortalDynamicConfigParams): ChatSalesPortalDynamicConfigResult => {
  const { supplierDynamicConfig, loading: supplierLoading } =
    useSupplierDynamicConfigLoader({ supplierUUID });

  const { chatDynamicConfig, loading: chatLoading } =
    useChatDynamicConfigLoader({ chatUUID });

  return {
    chatDynamicConfig,
    loading: supplierLoading || chatLoading,
    supplierDynamicConfig,
  };
};

/**
 * Get a specific dynamic config value for a given chat / supplier pair
 * Falls back through: chat config -> supplier config -> null.
 *
 * @param supplierUUID - The supplier identifier
 * @param key - The dynamic config key to retrieve
 * @param chatUUID - Optional chat identifier for chat-specific config
 * @returns An object containing the config value and loading state
 *
 * @example
 * ```typescript
 * const { loading, value } = useChatSalesPortalDynamicConfigValue(
 *   'supplier-789',
 *   'experiments',
 *   'chat-456',
 * );
 * ```
 */
export const useChatSalesPortalDynamicConfigValue = <
  K extends keyof DynamicConfig,
>(
  supplierUUID: string,
  key: K,
  chatUUID: string | null | undefined,
): { loading: boolean; value: DynamicConfig[K] | null } => {
  const { chatDynamicConfig, loading, supplierDynamicConfig } =
    useChatSalesPortalDynamicConfig({
      chatUUID,
      supplierUUID,
    });

  const value = useMemo(() => {
    if (loading) {
      return null;
    }
    return chatDynamicConfig?.[key] ?? supplierDynamicConfig?.[key] ?? null;
  }, [chatDynamicConfig, key, loading, supplierDynamicConfig]);

  return {
    loading,
    value,
  };
};
